
import { Vue, Component, Watch } from 'vue-property-decorator';
import WhereShipment from '@/features/estimate/components/WhereShipment.vue';
import WhatShipment from '@/features/shipment/components/createShipment/WhatShipment.vue';
import ExtrasShipment, { FormValidationType } from '@/features/shipment/components/createShipment/ExtrasShipment.vue';
import { scrollToInPage } from '@/features/common/helpers';
import { isReadyForEstimatePrefetch, createPrefetcher } from '@/features/common/helpers/prefetch';
import { GetRatesData } from '@/features/shipment/types';
import moment from 'moment';
import { VForm, VInput } from '@/features/common/types';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { safeListedCountryCodes } from '@/features/common/raw';
import { EstimateDataType, EstimateShipAddressType } from '../types';
import { validateAndScrollToError } from '../helpers/getEstimate';

@Component({
  components: {
    WhereShipment,
    WhatShipment,
    ExtrasShipment,
  },
})
export default class GetEstimate extends Vue {
  private countryCodesToHideCityInput = [...safeListedCountryCodes];

  private scrollToInPage = scrollToInPage;

  private validationMessage: FormValidationType = {
    isValid: true,
    message: '',
    isHint: true,
  };

  private isLoadingEstimate = false;

  private isSubmitting = false;

  private prefetcher = createPrefetcher('Rates');

  private fromData: EstimateShipAddressType = {
    countryCode: '',
    postalCode: '',
    city: '',
    isInside: false,
    isLimitedAccess: false,
    isTailGate: false,
    isTradeShow: false,
  }

  private toData: EstimateShipAddressType = {
    countryCode: '',
    postalCode: '',
    city: '',
    residential: false,
    isInside: false,
    isLimitedAccess: false,
    isTailGate: false,
    isTradeShow: false,
  }

  // eslint-disable-next-line class-methods-use-this
  get shipmentSection(): HTMLElement {
    return document.querySelector('.estimate') as HTMLElement;
  }

  get showFromCityInput(): boolean {
    const { countryCode } = this.fromData;
    return !!countryCode && !this.countryCodesToHideCityInput.includes(countryCode);
  }

  get showToCityInput(): boolean {
    const { countryCode } = this.toData;
    return !!countryCode && !this.countryCodesToHideCityInput.includes(countryCode);
  }

  get isEstimateInternational(): boolean {
    const { countryCode: from } = this.fromData;
    const { countryCode: to } = this.toData;
    return !!from && !!to && from !== to;
  }

  get estimateDataFromStore(): EstimateDataType {
    return this.$store.state.estimate.estimateData;
  }

  get whereShipmentForm(): VForm {
    return this.$store.state.estimate.estimateTopForm as VForm;
  }

  get whatShipmentForm(): VForm {
    return this.$store.state.shipment.whatShipmentForm as VForm;
  }

  get isValidFormQuery(): boolean {
    return this.$route.query.isValid !== 'false';
  }

  @Watch('validationMessage', { deep: true })
  private hideValidationMessageAfterFewSeconds(message: FormValidationType) {
    if (message.isValid) return;
    setTimeout(() => {
      this.resetValidationMessage();
    }, 7000);
  }

  @Watch('fromData', { deep: true })
  @Watch('toData', { deep: true })
  @Watch('estimateDataFromStore', { deep: true })
  private triggerPrefetch() {
    if (this.isSubmitting) return;

    const { packages } = this.estimateDataFromStore;
    if (!isReadyForEstimatePrefetch({
      fromAddress: this.fromData,
      toAddress: this.toData,
      packages,
    })) return;

    this.prefetcher({
      currencyCode: 'CAD',
      fromAddress: {
        countryCode: this.fromData.countryCode,
        postalCode: this.fromData.postalCode,
        addr1: '',
        city: this.fromData.city || '',
      },
      toAddress: {
        countryCode: this.toData.countryCode,
        postalCode: this.toData.postalCode,
        addr1: '',
        city: this.toData.city || '',
      },
      packages,
      shipDateTime: moment().format('YYYY-MM-DD'),
      documentsOnly: this.estimateDataFromStore.isDocumentsOnly,
    });
  }

  mounted(): void {
    this.scrollToInPage();
    this.submitIfInvalidInEstimate();
  }

  private async handleSubmit(isExtrasFormValid: boolean) {
    const isWhereFormValid = this.isWhereShipmentFormValid();
    const isWhatFormValid = this.isWhatShipmentFormValid();

    if (!isWhereFormValid || !isWhatFormValid || !isExtrasFormValid) {
      this.showValidationError();
      return;
    }

    eventBus.$emit(eventNames.SUBMIT_WHAT_SHIPMENT);
  }

  private showValidationError() {
    const errors: { id: string; errorMessage: string | undefined }[] = [];

    const collectErrors = (form: VForm) => {
      if (!form || !form.inputs) return;
      form.inputs.forEach((input: VInput) => {
        if (input.hasError) {
          errors.push({ id: input.id || '', errorMessage: input.validations?.[0] });
        }
      });
    };

    collectErrors(this.whereShipmentForm);
    collectErrors(this.whatShipmentForm);

    if (!this.estimateDataFromStore.signatureOptions) {
      errors.push({ id: 'signature-options-add-package', errorMessage: 'Required!' });
    }

    const message = validateAndScrollToError(errors);

    this.showValidationMessage({
      isValid: false,
      message: message || '',
      isHint: true,
    });
  }

  private handleWhatShipmentSubmit(isValid: boolean) {
    if (!isValid) return;

    this.continueToEstimate();
  }

  private async continueToEstimate() {
    this.isSubmitting = true;
    this.isLoadingEstimate = true;

    const shipDateTime = new Date().toISOString();

    this.$store.commit('patchInProgress', { key: 'shipDate', value: shipDateTime });

    const {
      isDocumentsOnly,
      signatureOptions,
      packages,
      fromAddress,
      toAddress,
    } = this.estimateDataFromStore;

    const finalFromData = {
      ...this.fromData,
      city: this.showFromCityInput ? this.fromData.city || '' : '',
      addr1: '',
      isInside: fromAddress.isInside,
      isLimitedAccess: fromAddress.isLimitedAccess,
      isTailGate: fromAddress.isTailGate,
      isTradeShow: fromAddress.isTradeShow,
    };

    const finalToData = {
      ...this.toData,
      city: this.showToCityInput ? this.toData.city || '' : '',
      addr1: '',
      isInside: toAddress.isInside,
      isLimitedAccess: toAddress.isLimitedAccess,
      isTailGate: toAddress.isTailGate,
      isTradeShow: toAddress.isTradeShow,
      appointment: toAddress.appointment,
    };

    const data: GetRatesData = {
      currencyCode: 'CAD',
      fromAddress: finalFromData,
      toAddress: finalToData,
      packages,
      shipDateTime,
      documentsOnly: isDocumentsOnly,
    };

    this.$store.commit('setEstimateData', {
      fromAddress: data.fromAddress,
      toAddress: data.toAddress,
      signatureOptions,
      packages: data.packages,
      isDocumentsOnly: data.documentsOnly,
    });

    this.$router.push({ name: 'estimate-results' });
    this.$emit('next');

    this.isLoadingEstimate = false;
  }

  private isWhereShipmentFormValid() {
    if (!this.whereShipmentForm) return false;

    return this.whereShipmentForm.validate();
  }

  private isWhatShipmentFormValid() {
    const { packages } = this.estimateDataFromStore;
    const packagesLength = packages ? packages.length : 0;

    if (packagesLength) return true;

    if (!this.whatShipmentForm) return false;

    const isValid = this.whatShipmentForm.validate();
    return isValid;
  }

  private showValidationMessage(validationMessage: FormValidationType) {
    this.validationMessage = validationMessage;
  }

  private resetValidationMessage() {
    this.validationMessage = {
      isValid: true,
      message: '',
      isHint: true,
    };
  }

  private submitIfInvalidInEstimate() {
    if (this.isValidFormQuery) return;

    eventBus.$emit(eventNames.SHIPMENT_SUBMIT);
    this.$router.push({ query: {} });
  }
}
